/**
 * Buchungsregeln für den Anfragekalender – framework-unabhängig (Vanilla JS).
 * Alle Datumswerte als "YYYY-MM-DD" (lokale Kalendertage, keine Uhrzeit).
 * Eine "Nacht" N gehört zum Kalendertag N (Anreise am Tag N, Nacht von N auf N+1).
 *
 * Regeln:
 * 1. Mindestaufenthalt = Mindestaufenthalt der Saison des ANREISETAGS.
 * 2. Anreisetag muss ein erlaubter Wochentag der Saison des Anreisetags sein.
 * 3. Abreisetag muss ein erlaubter Wochentag der Saison des ABREISETAGS sein.
 * 4. Keine belegte Nacht im Zeitraum [Anreise, Abreise). Wechseltage sind erlaubt.
 *
 * Eingabedaten (wie in rentalRequest.js / _inquiry.html.twig):
 * - seasons:      [{ start, end (EXKLUSIV, entspricht dem Start der Folgesaison), checkin, checkout, minNights, standard }]
 * - occupiedDays: calendar_occupied_days (Tage, die belegt sind)
 * - checkinDays:  calendar_checkin_available_days (belegte Tage, an denen trotzdem
 *                 angereist werden kann = Abreisetag des Vorgasts)
 *   => Nacht N ist belegt, wenn N in occupiedDays und nicht in checkinDays liegt.
 *
 * Die Gründe werden als { code, params } geliefert und im UI übersetzt.
 */

const DAY = 864e5

/**
 * Maps a CheckinOut enum value to JS day-of-week numbers (0=Sun … 6=Sat).
 * NA and FLEXIBEL both mean "no restriction" → all 7 days returned.
 */
export const checkinOutToDayNumbers = (value) => ({
    FLEXIBEL: [0, 1, 2, 3, 4, 5, 6],
    NA:       [0, 1, 2, 3, 4, 5, 6],
    MON: [1], TUE: [2], WED: [3], THU: [4],
    FRI: [5], SAT: [6], SUN: [0],
    'MO+FR': [1, 5],
}[value] ?? [0, 1, 2, 3, 4, 5, 6])

const toIdx = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number)
    return Math.round(Date.UTC(y, m - 1, d) / DAY)
}
const toYmdStr = (i) => new Date(i * DAY).toISOString().slice(0, 10)
const weekday = (i) => new Date(i * DAY).getUTCDay()

export function createBookingRules({
                                       seasons = [],
                                       occupiedDays = [],
                                       checkinDays = [],
                                       today,
                                       horizonDays = 3 * 365,
                                   } = {}) {
    const occupied = new Set(occupiedDays)
    const checkin = new Set(checkinDays)
    const standard = seasons.find((s) => s.standard) ?? null
    const periods = seasons
        .filter((s) => !s.standard && s.start && s.end)
        .map((s) => ({ season: s, a: toIdx(s.start), b: toIdx(s.end) }))

    const todayIdx = toIdx(today)
    const horizon = todayIdx + horizonDays
    const bookedCache = new Map()
    const arrivalCache = new Map()

    const seasonAt = (i) => {
        for (const p of periods) if (i >= p.a && i < p.b) return p.season
        return standard
    }

    const isBookedNight = (i) => {
        let r = bookedCache.get(i)
        if (r === undefined) {
            const s = toYmdStr(i)
            r = occupied.has(s) && !checkin.has(s)
            bookedCache.set(i, r)
        }
        return r
    }

    const arrivalAllowed = (i) => checkinOutToDayNumbers(seasonAt(i)?.checkin).includes(weekday(i))
    const departureAllowed = (i) => checkinOutToDayNumbers(seasonAt(i)?.checkout).includes(weekday(i))
    const minNightsFor = (i) => Math.max(1, seasonAt(i)?.minNights ?? 0)

    /** Prüft [Anreise, Abreise) ohne Anreise-Wochentag. { ok, code, params } */
    function validate(s, e) {
        if (e <= s) return { ok: false, code: 'afterArrival' }
        for (let i = s; i < e; i++) {
            if (isBookedNight(i)) return { ok: false, code: 'unavailable' }
        }
        if (!departureAllowed(e)) return { ok: false, code: 'departureDay' }
        const min = minNightsFor(s)
        if (e - s < min) return { ok: false, code: 'minNights', params: { min } }
        return { ok: true }
    }

    /** Gibt es ab dieser Anreise irgendeine gültige Abreise (bis zur nächsten Belegung)? */
    function hasDeparture(s) {
        for (let e = s + 1; e <= horizon; e++) {
            if (isBookedNight(e - 1)) return false
            if (validate(s, e).ok) return true
        }
        return false
    }

    /** Ist der Tag als Anreise wählbar? { disabled, code } */
    function arrivalState(ymd) {
        let r = arrivalCache.get(ymd)
        if (r) return r
        const i = toIdx(ymd)
        if (i < todayIdx) r = { disabled: true, code: 'past' }
        else if (isBookedNight(i)) r = { disabled: true, code: 'booked' }
        else if (!arrivalAllowed(i)) r = { disabled: true, code: 'arrivalDay' }
        else if (!hasDeparture(i)) r = { disabled: true, code: 'noDeparture' }
        else r = { disabled: false }
        arrivalCache.set(ymd, r)
        return r
    }

    /** Ist der Tag nach gewählter Anreise als Abreise wählbar? { disabled, code, params } */
    function departureState(arrivalYmd, ymd) {
        const r = validate(toIdx(arrivalYmd), toIdx(ymd))
        return r.ok ? { disabled: false } : { disabled: true, code: r.code, params: r.params }
    }

    return { arrivalState, departureState }
}

document.addEventListener("DOMContentLoaded", () => {
    initMobileInquiryButton();
    initUnitCalendars();

    if (!document.getElementById("availability-form")) {
        return;
    }

    initBookingDatepicker();
    initAvailabilityForm();
    initQuantitySteppers();
    initInquiryModal();
    syncAvailabilityUiOnPageLoad();
    initRentalUnitSelection();
    initFeedbackModal();
});

/**
 * Bind inputs outside of modal to hidden modal form fields
 */
const mirrorFormInputs = () => {
    Object.entries({
        booking_adults: "adults",
        booking_children: "children",
        booking_dogs: "dogs",
        booking_dates: "checkinCheckoutDates",
        booking_rentalUnitId: "rentalUnitId",
    }).forEach(([formFieldId, inputId]) => {
        const sourceInput = document.getElementById(inputId);
        const targetField = document.getElementById(formFieldId);

        if (!sourceInput || !targetField) {
            return;
        }

        if (
            targetField.tagName === "SELECT" &&
            formFieldId === "booking_dogs" &&
            sourceInput.value &&
            !Array.from(targetField.options).some(
                (o) => o.value === String(sourceInput.value)
            )
        ) {
            const val = parseInt(sourceInput.value, 10) || 0;
            const max = Math.max(5, val);
            targetField.replaceChildren();
            for (let i = 0; i <= max; i++) {
                const opt = document.createElement("option");
                opt.value = String(i);
                opt.textContent = global.Translator?.transChoice
                    ? global.Translator.transChoice(
                        "booking.fields.pets.values",
                        i,
                        { count: i },
                        "js"
                    )
                    : i === 0
                        ? "Keine"
                        : `${i} Haustier${i > 1 ? "e" : ""}`;
                targetField.appendChild(opt);
            }
        }

        targetField.value = sourceInput.value;
        targetField.dispatchEvent(new Event("change"));
    });
};

const toYmd = (value) => {
    if (!value) {
        return null;
    }

    const d = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(d.getTime())) {
        return null;
    }

    // Use local date parts to avoid UTC-based day shifts from toISOString().
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

/**
 * Texts for rule violations (codes come from bookingRules.js).
 * Falls back to German if the translation is missing.
 */
const bookingRuleReason = (code, params = {}) => {
    const fallbacks = {
        past: "Liegt in der Vergangenheit.",
        booked: "Dieser Tag ist belegt.",
        arrivalDay: "Anreise ist an diesem Tag nicht möglich.",
        noDeparture: "Ab diesem Tag ist keine gültige Abreise möglich.",
        unavailable: "Der Zeitraum enthält belegte Nächte.",
        departureDay: "Abreise ist an diesem Tag nicht möglich.",
        afterArrival: "Das Abreisedatum muss nach der Anreise liegen.",
        minNights: "Mindestaufenthalt: %min% Nächte.",
    };

    const key = `booking.rules.${code}`;
    const translated = global.Translator.trans(key, { "%min%": params.min }, "js");
    const text = translated !== key ? translated : (fallbacks[code] ?? "");

    return text.replace("%min%", params.min);
};

/**
 * Returns the matching season for a given date, falling back to the standard season.
 */
const getSeasonForDate = (date, seasons) => {
    const ymd = toYmd(date);

    return (
        seasons.find((s) => !s.standard && s.start && s.end && ymd >= s.start && ymd < s.end)
        ?? seasons.find((s) => s.standard)
        ?? null
    );
};

/**
 * Returns true if any season has weekday restrictions or a minimum-nights requirement.
 */
const hasActiveSeasonRestrictions = (seasons) =>
    seasons.some((s) =>
        (s.checkin !== "NA" && s.checkin !== "FLEXIBEL") ||
        (s.checkout !== "NA" && s.checkout !== "FLEXIBEL") ||
        (s.minNights > 0)
    );

const checkAvailability = async () => {
    const availabilityForm = document.getElementById("availability-form");

    mirrorFormInputs();

    try {
        const dates = document.querySelector("#checkinCheckoutDates")._flatpickr.selectedDates;

        if (dates.length >= 2) {
            setAvailabilityLoading();
        }

        const params = new URLSearchParams();
        params.append("fromDate", toYmd(dates[0]));
        params.append("toDate", toYmd(dates[1]));

        const unitSelect = document.getElementById("rentalUnitId");
        if (unitSelect?.value) {
            params.append("rental_unit_id", unitSelect.value);
        }

        const availabilityUrl = new URL(availabilityForm.action);
        params.forEach((value, key) => availabilityUrl.searchParams.set(key, value));

        const response = await fetch(availabilityUrl.toString(), {
            method: "GET",
            headers: {
                "X-Requested-With": "XMLHttpRequest",
                Accept: "application/json",
            },
        });

        if (!response.ok) {
            throw new Error("Availability request failed");
        }

        const result = await response.json();

        handleAvailabilityInfoCard(result, dates);

        if (dates && dates.length === 2) {
            const d0 = dates[0] instanceof Date ? dates[0] : new Date(dates[0]);
            const d1 = dates[1] instanceof Date ? dates[1] : new Date(dates[1]);
            const days = Math.round((d1 - d0) / (1000 * 60 * 60 * 24));

            window.dispatchEvent(
                new CustomEvent("p4g-price-recalc", {
                    detail: {
                        dates: [d0, d1],
                        days: days,
                        adults: parseInt(document.getElementById("adults")?.value || 1, 10),
                        children: parseInt(document.getElementById("children")?.value || 0, 10),
                        rentalUnitId: unitSelect?.value || document.getElementById("rentalUnitId")?.value,
                    },
                })
            );
        }
    } catch (error) {
        console.error(error);
    }
};

/**
 * Show a neutral loading state while the availability request is running
 */
const setAvailabilityLoading = () => {
    const loadingKey = "booking.card.checking";
    const translated = global.Translator.trans(loadingKey, {}, "js");
    const message = document.getElementById("availabilityinfo-message");

    document.getElementById("availabilityinfonote")?.classList.add("hidden");
    message?.classList.remove("text-red-500");

    if (message) {
        message.innerHTML = translated !== loadingKey ? translated : "Verfügbarkeit wird geprüft …";
    }

    const reason = document.getElementById("availabilityinfo-reason");
    if (reason) {
        reason.innerHTML = "";
    }

    document.getElementById("availabilityinfo-button")?.toggleAttribute("disabled", true);
};

/**
 * build AvailabilityInfoCard
 */
const handleAvailabilityInfoCard = (result, dates) => {
    const availabilityInfoNote = document.getElementById("availabilityinfonote");
    const message = document.getElementById("availabilityinfo-message");
    const reason = document.getElementById("availabilityinfo-reason");
    const requestButton = document.getElementById("availabilityinfo-button");

    if (message) {
        message.innerHTML = result.message ?? "";
    }

    if (reason) {
        reason.innerHTML = result.reason ?? "";
    }

    if (result.isAvailable) {
        message?.classList.remove("text-red-500");
        requestButton?.toggleAttribute("disabled", false);
        availabilityInfoNote?.classList.add("hidden");
    } else if (dates && dates.length >= 2) {
        message?.classList.add("text-red-500");
        requestButton?.toggleAttribute("disabled", true);
    }

    if (result.availableUnitIds && Array.isArray(result.availableUnitIds)) {
        const availableIds = result.availableUnitIds.map((id) => String(id));

        document.querySelectorAll(".js-rental-unit-card").forEach((card) => {
            if (card.getAttribute("data-calendar-known") === "false") return;

            const uId = card.getAttribute("data-unit-id");
            const badge = card.querySelector(".js-unit-availability-badge");
            const dot = card.querySelector(".js-status-dot");
            const text = card.querySelector(".js-status-text");

            if (!badge || !dot || !text) return;

            if (dates && dates.length >= 2) {
                if (availableIds.includes(String(uId))) {
                    badge.className =
                        "badge badge--xs badge--pill bg-emerald-50 text-emerald-800 border border-emerald-200 js-unit-availability-badge";
                    dot.className =
                        "inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1 js-status-dot";
                    text.textContent = "Verfügbar";
                } else {
                    badge.className =
                        "badge badge--xs badge--pill bg-rose-50 text-rose-800 border border-rose-200 js-unit-availability-badge";
                    dot.className =
                        "inline-block w-2 h-2 rounded-full bg-rose-500 mr-1 js-status-dot";
                    text.textContent = "Nicht verfügbar";
                }
            } else {
                badge.className =
                    "badge badge--xs badge--pill bg-gray-100 text-brandcopy-700 js-unit-availability-badge";
                dot.className =
                    "inline-block w-2 h-2 rounded-full bg-gray-400 mr-1 js-status-dot";
                text.textContent = "Verfügbarkeit auf Anfrage";
            }
        });
    }
};

/**
 * Booking Datepicker
 */
const initBookingDatepicker = () => {
    const storedValues = window.localStorage.getItem("locationFormState")
        ? JSON.parse(window.localStorage.getItem("locationFormState"))
        : {};
    const input = document.querySelector("#checkinCheckoutDates");

    if (!input) {
        return;
    }

    // Auto Submit form if date is set
    if (storedValues.bookingFrom && storedValues.bookingTo) {
        window.setTimeout(() => {
            checkAvailability();
        }, 500);
    }

    const showTimespanAlert = () => {
        const alert = document.getElementById("inquiry-alert");
        if (!alert) return;

        const title = alert.querySelector(".alert__content-title span");
        const content = alert.querySelector(".alert__content-content span");

        if (title) {
            title.innerHTML = global.Translator.trans("booking.invalid_timespan", {}, "js");
        }
        if (content) {
            content.innerHTML = global.Translator.trans("booking.select_timespan", {}, "js");
        }

        alert.classList.remove("hidden");
    };

    const options = {
        dateFormat: "d.m.Y",
        mode: "range",
        defaultDate: [
            storedValues.bookingFrom !== undefined ? storedValues.bookingFrom : null,
            storedValues.bookingTo !== undefined ? storedValues.bookingTo : null,
        ],
        showMonths: window.matchMedia("(max-width: 767px)").matches ? 1 : 2,
        minDate: "today",
        maxDate: new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000),
        onChange: function (selectedDates) {
            if (selectedDates && selectedDates.length === 2) {
                input.dispatchEvent(new Event("change", { bubbles: true }));
            }
        },
        onClose: function (selectedDates, dateStr, instance) {
            if (selectedDates.length < 2) {
                instance.clear();
                showTimespanAlert();

                return;
            }

            const start = selectedDates[0].getTime();
            const end = selectedDates[1].getTime();

            if (start === end) {
                instance.clear();
                showTimespanAlert();

                return;
            }

            input.dispatchEvent(new Event("change", { bubbles: true }));
            document.getElementById("inquiry-alert")?.classList.add("hidden");
            checkAvailability();
        },
    };

    // Parsed up front so the season rules below can use them as well
    const occupiedDays = JSON.parse(input.getAttribute("data-occupied-days") || "[]");
    const checkinDays = JSON.parse(input.getAttribute("data-checkin-days") || "[]");
    const checkoutDays = JSON.parse(input.getAttribute("data-checkout-days") || "[]");

    if (input.hasAttribute("data-occupied-days")) {
        options.disable = [
            function (date) {
                const dateString = toYmd(date);
                const isOccupied = occupiedDays.includes(dateString);
                const isCheckin = checkinDays.includes(dateString);
                const isCheckout = checkoutDays.includes(dateString);

                return isOccupied && !isCheckin && !isCheckout;
            },
        ];
    }

    // Season-based weekday + min-nights restrictions
    if (input.hasAttribute("data-season-rules")) {
        const seasonRules = JSON.parse(input.getAttribute("data-season-rules") || "[]");

        if (hasActiveSeasonRestrictions(seasonRules)) {
            const rules = createBookingRules({
                seasons: seasonRules,
                occupiedDays,
                checkinDays,
                today: toYmd(new Date()),
            });

            /**
             * Returns the rule violation { code, params } for a day, or null if selectable.
             *
             * NOTE: These rules intentionally do NOT use flatpickr's `disable` option.
             * In range mode flatpickr treats every day after the first disabled day
             * as "notAllowed", which made the whole calendar unclickable after picking
             * the arrival day. Instead, days are blocked via the `fp-day--blocked`
             * class (see rental.scss) in onDayCreate; clicks on them are intercepted
             * in onReady to show the reason.
             */
            const getViolation = (date, selectedDates) => {
                const state = selectedDates.length === 1
                    ? rules.departureState(toYmd(selectedDates[0]), toYmd(date))
                    : rules.arrivalState(toYmd(date));

                return state.disabled ? state : null;
            };

            const hasDayRestrictions = seasonRules.some((s) =>
                (s.checkin !== "NA" && s.checkin !== "FLEXIBEL") ||
                (s.checkout !== "NA" && s.checkout !== "FLEXIBEL")
            );

            options.onDayCreate = function (dObj, dStr, fp, dayElem) {
                if (dayElem.classList.contains("flatpickr-disabled")) return;

                const date = dayElem.dateObj;
                const selectedDates = fp.selectedDates;
                const isSelected = selectedDates.some((d) => toYmd(d) === toYmd(date));

                const violation = isSelected ? null : getViolation(date, selectedDates);
                if (violation) {
                    dayElem.classList.add("fp-day--blocked");
                    dayElem.dataset.fpReason = violation.code;
                    if (violation.params?.min !== undefined) {
                        dayElem.dataset.fpReasonMin = violation.params.min;
                    }

                    return;
                }

                if (!hasDayRestrictions) return;

                const season = getSeasonForDate(date, seasonRules);
                if (!season) return;

                if (selectedDates.length === 1) {
                    if (checkinOutToDayNumbers(season.checkout).length < 7) {
                        dayElem.classList.add("fp-day--checkout");
                    }
                } else if (checkinOutToDayNumbers(season.checkin).length < 7) {
                    dayElem.classList.add("fp-day--checkin");
                }
            };

            options.onReady = function (selectedDates, dateStr, fp) {
                const minNightsValues = [...new Set(seasonRules.map((s) => s.minNights).filter((n) => n > 0))].sort((a, b) => a - b);
                const minStayText = minNightsValues.length === 0
                    ? ""
                    : minNightsValues.length === 1
                        ? `Mindestaufenthalt: ${minNightsValues[0]} Nächte`
                        : `Mindestaufenthalt: ${minNightsValues[0]}–${minNightsValues[minNightsValues.length - 1]} Nächte (je nach Saison)`;

                const legend = document.createElement("div");
                legend.className = "fp-legend";
                legend.innerHTML =
                    (hasDayRestrictions
                        ? '<span class="fp-legend__item"><span class="fp-legend__dot fp-legend__dot--checkin"></span>Anreisetag</span>' +
                        '<span class="fp-legend__item"><span class="fp-legend__dot fp-legend__dot--checkout"></span>Abreisetag</span>'
                        : "") +
                    (minStayText ? `<span class="fp-legend__item">${minStayText}</span>` : "") +
                    '<span class="fp-legend__item">Ausgegraut: belegt oder nicht wählbar (Mindestaufenthalt / Wochentag)</span>';
                fp.calendarContainer.appendChild(legend);

                // Clicking a blocked day keeps the selection and shows the reason instead
                fp.calendarContainer.addEventListener("click", (e) => {
                    const day = e.target.closest(".flatpickr-day.fp-day--blocked");
                    if (!day) return;

                    e.stopPropagation();
                    e.preventDefault();

                    const message = document.getElementById("availabilityinfo-message");
                    if (!message) return;

                    message.textContent = bookingRuleReason(day.dataset.fpReason, { min: day.dataset.fpReasonMin });
                    message.classList.add("text-red-500");
                    message.dataset.fpHint = "1";
                }, true);
            };

            // Re-render days so blocked days/markers reflect the current selection state.
            // Keeps the change dispatch from the base options above.
            options.onChange = function (selectedDates, dateStr, instance) {
                if (selectedDates && selectedDates.length === 2) {
                    input.dispatchEvent(new Event("change", { bubbles: true }));
                }

                const message = document.getElementById("availabilityinfo-message");
                if (message?.dataset.fpHint) {
                    message.textContent = "";
                    message.classList.remove("text-red-500");
                    delete message.dataset.fpHint;
                }

                instance.redraw();
            };
        }
    }

    flatpickr(input, options);
};

/**
 * Availability Check
 */
const initAvailabilityForm = () => {
    const storedValues = window.localStorage.getItem("locationFormState")
        ? JSON.parse(window.localStorage.getItem("locationFormState"))
        : {};
    const availabilityForm = document.getElementById("availability-form");

    /**
     * Default value
     */
    if (storedValues.kids !== undefined) {
        availabilityForm.querySelector("#children").value = storedValues.kids;
        document.querySelector("#booking_children").value = storedValues.kids;
    }
    if (storedValues.adults !== undefined) {
        availabilityForm.querySelector("#adults").value = storedValues.adults;
        document.querySelector("#booking_adults").value = storedValues.adults;
    }
    if (storedValues.dogs !== undefined) {
        availabilityForm.querySelector("#dogs").value = storedValues.dogs;
        document.querySelector("#booking_dogs").value = storedValues.dogs;
    }

    availabilityForm.querySelectorAll("input[type=number]").forEach((el) =>
        el.addEventListener("change", () => {
            availabilityForm.dispatchEvent(new SubmitEvent("submit"));
        })
    );

    availabilityForm.addEventListener("submit", async function (e) {
        e.preventDefault();
        e.stopPropagation();

        await checkAvailability();
    });
};

/**
 * Quantity Steppers - special version that considers the max capacity
 */
const initQuantitySteppers = () => {
    const adultsInput = document.getElementById("adults");
    const childrenInput = document.getElementById("children");

    document.querySelectorAll(".quantitystepper").forEach((stepper) => {
        const input = stepper.querySelector('input[type="number"]');
        const btnDecrease = stepper.querySelector(".quantitystepper__control-button--decrease");
        const btnIncrease = stepper.querySelector(".quantitystepper__control-button--increase");

        if (!input) return;

        input.addEventListener("input", () => {
            mirrorFormInputs();
        });

        input.addEventListener("change", () => {
            if (input.id !== "adults" && input.id !== "children") return;

            const maxCapacity = parseInt(adultsInput?.getAttribute("max"), 10) || 0;
            if (maxCapacity <= 0) return;

            let adults = parseInt(adultsInput?.value, 10) || 0;
            let children = parseInt(childrenInput?.value, 10) || 0;

            if (adults > maxCapacity && adultsInput) {
                adultsInput.value = String(maxCapacity);
                adults = maxCapacity;
            }
            if (adults + children > maxCapacity && childrenInput) {
                childrenInput.value = String(Math.max(0, maxCapacity - adults));
            }

            mirrorFormInputs();
        });

        /**
         * Minus
         */
        btnDecrease?.addEventListener("click", () => {
            const min = parseInt(input.getAttribute("min")) || 0;
            const value = parseInt(input.value) || 0;

            if (value > min) {
                input.value = value - 1;
                input.dispatchEvent(new Event("input", { bubbles: true }));
            }
        });

        /**
         * Plus
         */
        btnIncrease?.addEventListener("click", () => {
            const value = parseInt(input.value) || 0;
            const max = parseInt(input.getAttribute("max"));

            /**
             * adults + children combined limit
             */
            if (input.id === "adults" || input.id === "children") {
                const maxCapacity = parseInt(adultsInput.getAttribute("max")) || 0;
                const adults = parseInt(adultsInput.value) || 0;
                const children = parseInt(childrenInput.value) || 0;
                const total = adults + children;

                if (total < maxCapacity) {
                    input.value = value + 1;
                    input.dispatchEvent(new Event("input", { bubbles: true }));
                }
            } else {
                /**
                 * dog stepper
                 */
                if (isNaN(max) || value < max) {
                    input.value = value + 1;
                    input.dispatchEvent(new Event("input", { bubbles: true }));
                }
            }
        });
    });
};

/**
 * Inquiry Modal
 */
const initInquiryModal = () => {
    const inquiryForm = document.getElementById("inquiry-form");

    if (!inquiryForm) {
        return;
    }

    const steps = [
        document.getElementById("inquiry-step-1"),
        document.getElementById("inquiry-step-2"),
        document.getElementById("inquiry-step-3"),
    ];

    let currentStep = 0;
    const submitButton = inquiryForm.querySelector(
        "#booking_submit, #inquiry-submit, button[type='submit']"
    );
    let submissionInProgress = false;
    let hasSuccessfulSubmission = false;

    const messageTextarea = inquiryForm.querySelector("#booking_message");
    const resizeMessage = () => {
        if (!messageTextarea) return;

        messageTextarea.style.height = "auto";

        const minHeight = 60;
        const maxHeight = 220;
        const scrollHeight = messageTextarea.scrollHeight;
        const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

        messageTextarea.style.height = `${newHeight}px`;
        messageTextarea.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
    };

    if (messageTextarea) {
        messageTextarea.addEventListener("input", resizeMessage);
        messageTextarea.addEventListener("change", resizeMessage);
    }

    const enableSubmitAfterEdit = () => {
        if (hasSuccessfulSubmission && !submissionInProgress && submitButton) {
            submitButton.removeAttribute("disabled");
        }
    };

    const availabilityButton = document.getElementById("availabilityinfo-button");
    if (availabilityButton) {
        availabilityButton.addEventListener("click", () => {
            mirrorFormInputs();
        });
    }

    inquiryForm.addEventListener("input", enableSubmitAfterEdit);
    inquiryForm.addEventListener("change", enableSubmitAfterEdit);

    const showStep = (idx) => {
        steps.forEach((step, i) => {
            step?.classList.toggle("hidden", i !== idx);
        });

        document.getElementById("inquiry-prev")?.classList.toggle("hidden", idx === 0);
        document.getElementById("inquiry-next")?.classList.toggle("hidden", idx === steps.length - 1);

        if (submitButton) {
            submitButton.classList.toggle("hidden", idx !== steps.length - 1);
        }

        if (idx === steps.length - 1) {
            // Recalculate height once the last step is visible in the DOM
            setTimeout(resizeMessage, 50);
        }

        // Dotnav im Modal synchronisieren
        const inquiryModal = document.getElementById("inquiry-modal");
        const dotNavItems = inquiryModal
            ? inquiryModal.querySelectorAll(".dotnav .dotnav__item")
            : document.querySelectorAll("#inquiry-modal .dotnav .dotnav__item");

        dotNavItems.forEach((item, i) => {
            item.classList.toggle("dotnav__item--active", i <= idx);
        });

        currentStep = idx;
    };

    /*
     The empty arrays in validationIds and validationClasses ensure that each form step
     has a corresponding entry, even if no fields need validation in that step.
     This keeps the index alignment consistent across all steps.
    */
    const validationIds = [
        ["booking_dates", "booking_dogTypes"],
        ["booking_name", "booking_surname", "booking_email", "booking_phone_number"],
        [],
    ];

    const validationClasses = [["booking_ageChildren"], [], []];

    const validateStep = (idx) => {
        // Every field is evaluated on purpose (no short-circuit) so reportValidity()
        // fires on each one.
        const byId = validationIds[idx].map((id) => {
            const el = document.getElementById(id);

            return !el || validateField(el);
        });

        const byClass = validationClasses[idx].flatMap((cls) =>
            Array.from(document.getElementsByClassName(cls)).map((el) => validateField(el))
        );

        return [...byId, ...byClass].every(Boolean);
    };

    const nextButton = document.getElementById("inquiry-next");
    const prevButton = document.getElementById("inquiry-prev");

    nextButton?.addEventListener("click", (e) => {
        e.preventDefault();

        const valid = validateStep(currentStep);

        if (currentStep < steps.length - 1 && valid) {
            showStep(currentStep + 1);
        }
    });

    prevButton?.addEventListener("click", (e) => {
        e.preventDefault();

        if (currentStep > 0) {
            showStep(currentStep - 1);
        }
    });

    inquiryForm.addEventListener("submit", async (e) => {
        e.preventDefault();

        if (submissionInProgress) return;

        submissionInProgress = true;
        submitButton?.setAttribute("disabled", "disabled");

        try {
            // Perform submission
            const response = await fetch(inquiryForm.action, {
                method: "POST",
                body: new FormData(inquiryForm),
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    Accept: "application/json",
                },
            });

            const responseBody = await response.json();

            if (!response.ok) {
                alert(responseBody.errors[0].message);
                submitButton?.removeAttribute("disabled");

                return;
            }

            hasSuccessfulSubmission = true;

            // Trigger datalayer
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "request_sent",
            });

            if (responseBody.reservation_created) {
                const titleEl = document.getElementById("requestsent-title");
                const subTitleEl = document.getElementById("requestsent-subtitle");
                const unverifiedEl = document.getElementById("requestsent-unverified-notice");
                const resBtnEl = document.getElementById("requestsent-reservations-btn");
                const nextStepsEl = document.getElementById("requestsent-next-steps");

                if (titleEl) titleEl.textContent = "Danke für Ihre Reservierung!";
                if (subTitleEl) subTitleEl.textContent = "Ihre Reservierungsanfrage wurde erfolgreich eingereicht und muss vom Vermieter bestätigt werden.";
                if (resBtnEl) resBtnEl.classList.remove("hidden");
                if (responseBody.reservation_unverified && unverifiedEl) {
                    unverifiedEl.classList.remove("hidden");
                }
                if (nextStepsEl) {
                    nextStepsEl.innerHTML = `
          <li>Ihre Reservierung wurde an die Unterkunft übermittelt.</li>
          <li>Der Vermieter prüft Ihre Reservierung und bestätigt diese in Kürze.</li>
          <li>Sie können den Status jederzeit in Ihrem Reservierungsbereich einsehen.</li>
        `;
                }
            }

            const inquiryModalEl = document.getElementById("inquiry-modal");
            const inquiryModalInstance = window.FlowbiteInstances?.getInstance("Modal", "inquiry-modal");

            if (inquiryModalInstance) {
                inquiryModalInstance.hide();
            } else {
                inquiryModalEl?.classList.add("hidden");
                inquiryModalEl?.classList.remove("flex");
            }

            setTimeout(() => {
                const requestsentEl = document.getElementById("requestsent-modal");
                const requestsentModalInstance = window.FlowbiteInstances?.getInstance("Modal", "requestsent-modal");

                if (requestsentModalInstance) {
                    requestsentModalInstance.show();
                } else if (requestsentEl) {
                    requestsentEl.classList.remove("hidden");
                    requestsentEl.classList.add("flex", "items-center", "justify-center");
                    document.body.classList.add("overflow-hidden");
                }
            }, 150);
        } catch (error) {
            console.error("Booking request failed", error);
            submitButton?.removeAttribute("disabled");
        } finally {
            submissionInProgress = false;
        }
    });

    // setup children age fields based on selected number of children
    const selectChildren = document.getElementById("booking_children");
    const container = document.getElementById("booking_ageChildren");
    const ageChildrenWrapper = document.getElementById("booking-ageChildren-container");

    // toggle invoice address fields based on checkbox
    const invoiceAddressCheckbox = document.getElementById("booking_invoiceAddressRequested");
    const invoiceAddressContainer = document.getElementById("invoice-address-container");

    if (invoiceAddressCheckbox && invoiceAddressContainer) {
        invoiceAddressCheckbox.addEventListener("change", function () {
            invoiceAddressContainer.classList.toggle("hidden", !this.checked);
        });
    }

    selectChildren?.addEventListener("change", function () {
        if (!container) return;

        const count = parseInt(this.value) || 0;

        container.innerHTML = "";
        ageChildrenWrapper?.classList.toggle("hidden", count < 1);

        for (let i = 0; i < count; i++) {
            const prototype = container.dataset.prototype.replace(/__name__/g, i);
            const fieldWrapper = document.createElement("div");
            const labelTemplate = global.Translator.trans(
                "booking.fields.children.counterAge",
                { "%counter%": "%counter%" },
                "js"
            );

            fieldWrapper.innerHTML = `
        <div class="inputfield md:col-span-2">
            <div class="inputfield__inputcontainer">
                <label class="inputfield__inputcontainer-label" for="booking_ageChildren_${i}">
                  ${labelTemplate.replace("%counter%", i + 1)}
                </label>

                ${prototype}
            </div>
        </div>
    `;

            container.appendChild(fieldWrapper);
        }
    });

    // set dogtypes as required if dogs > 0
    const dogTypesInput = document.getElementById("booking_dogTypes");
    const selectDogs = document.getElementById("booking_dogs");

    if (dogTypesInput && selectDogs) {
        selectDogs.addEventListener("change", () => {
            const parentInputfield = dogTypesInput.closest(".inputfield");

            parentInputfield?.classList.toggle("hidden", selectDogs.value <= 0);
            dogTypesInput.toggleAttribute("required", selectDogs.value > 0);
        });
    }

    showStep(0);
};

const validateField = (input) => {
    if (input.hasAttribute("required") && input.value.trim() === "") {
        input.classList.add("error");

        let hadReadonly = false;

        if (input.hasAttribute("readonly")) {
            input.removeAttribute("readonly");
            hadReadonly = true;
        }

        input.setCustomValidity(global.Translator.trans("booking.please_fill_field", {}, "js"));
        input.reportValidity();

        if (hadReadonly) {
            setTimeout(() => {
                input.setAttribute("readonly", "readonly");
            }, 100);
        }

        return false;
    }

    input.classList.remove("error");
    input.setCustomValidity("");

    return input.reportValidity();
};

const initMobileInquiryButton = () => {
    document
        .getElementById("availabilityinfo-button-mobile")
        ?.addEventListener("click", () => {
            const inquiryButton = document.querySelector("#availabilityinfo-button");

            if (!inquiryButton || inquiryButton.hasAttribute("disabled")) {
                document.querySelector("#inquiry")?.scrollIntoView({ behavior: "smooth" });

                return;
            }

            inquiryButton.click();
        });
};

const syncAvailabilityUiOnPageLoad = () => {
    const datepicker = document.querySelector("#checkinCheckoutDates")?._flatpickr;
    const dates = datepicker?.selectedDates ?? [];
    const availabilityinfonote = document.getElementById("availabilityinfonote");

    if (!Array.isArray(dates) || dates.length < 2) {
        availabilityinfonote?.classList.remove("hidden");
    }
};

const initUnitCalendars = () => {
    const mounts = document.querySelectorAll(".js-unit-calendar-mount");

    if (!mounts.length) {
        return;
    }

    const isXl = window.matchMedia("(min-width: 1400px)").matches;
    const isMd = window.matchMedia("(min-width: 768px)").matches;
    const isSm = window.matchMedia("(min-width: 640px)").matches;
    const monthLimit = isXl ? 4 : isMd ? 3 : isSm ? 2 : 1;

    mounts.forEach((mount) => {
        const unitId = mount.getAttribute("data-unit-id");
        if (!unitId) return;

        const calendar = document.createElement("p4g-yearly-calendar");
        calendar.classList.add("block");
        calendar.setAttribute("id", unitId);
        calendar.setAttribute("size", "compact");
        calendar.setAttribute("month-limit", String(monthLimit));
        calendar.setAttribute("available-color", "#d1fadf");
        calendar.setAttribute("booked-color", "#fff");
        mount.appendChild(calendar);
    });
};

const initRentalUnitSelection = () => {
    const unitSelect = document.getElementById("rentalUnitId");
    const unitButtons = document.querySelectorAll(".js-select-unit-btn");

    const openInquiryForUnit = (unitId, checkinDate = null) => {
        if (unitSelect && unitSelect.tagName === "SELECT") {
            unitSelect.value = unitId;
            unitSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }

        document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" });

        const dateInput = document.getElementById("checkinCheckoutDates");
        if (!dateInput) return;

        const datepicker = dateInput._flatpickr;
        if (datepicker && checkinDate instanceof Date && !Number.isNaN(checkinDate.getTime())) {
            const checkin = new Date(
                checkinDate.getFullYear(),
                checkinDate.getMonth(),
                checkinDate.getDate()
            );
            // flatpickr drops disabled dates (past/occupied) itself, leaving the range empty
            datepicker.setDate([checkin], false);
            datepicker.jumpToDate(checkin);
        }

        const openCalendar = () => {
            if (dateInput._flatpickr) {
                dateInput._flatpickr.open();
                dateInput._flatpickr.positionCalendar?.();
            }
            dateInput.focus();
        };

        openCalendar();
        setTimeout(openCalendar, 200);
        setTimeout(openCalendar, 400);

        if ("onscrollend" in window) {
            window.addEventListener("scrollend", openCalendar, { once: true });
        }
    };

    // Day click inside <p4g-availability>; event is composed, so target is retargeted
    // to the host element (id = unit id)
    window.addEventListener("p4g-availability-click", (e) => {
        const host = e.target?.closest?.("p4g-availability");
        const unitId = host?.getAttribute("id") || e.detail?.unitId;
        if (!unitId) return;

        // Clicking the slotted trigger button toggles (closes) the component's dialog
        host?.querySelector("button")?.click();

        openInquiryForUnit(unitId, e.detail?.date ?? null);
    });

    unitButtons.forEach((button) => {
        button.addEventListener("click", () => {
            const unitId = button.getAttribute("data-unit-id");
            if (!unitId) return;

            openInquiryForUnit(unitId);
        });
    });

    if (unitSelect && unitSelect.tagName === "SELECT") {
        unitSelect.addEventListener("change", () => {
            const selectedOpt = unitSelect.options[unitSelect.selectedIndex];
            if (!selectedOpt) return;

            const count = parseInt(selectedOpt.getAttribute("data-count") || "1", 10) || 1;
            const capacity = parseInt(selectedOpt.getAttribute("data-capacity") || "0", 10);
            const maxCapacity = parseInt(selectedOpt.getAttribute("data-max-capacity") || "0", 10) || (capacity * count);
            const adultsInput = document.getElementById("adults");
            const childrenInput = document.getElementById("children");

            if (maxCapacity && adultsInput) {
                adultsInput.setAttribute("max", String(maxCapacity));
                if (parseInt(adultsInput.value, 10) > maxCapacity) {
                    adultsInput.value = String(maxCapacity);
                    adultsInput.dispatchEvent(new Event("change"));
                }
            }
            if (maxCapacity && childrenInput) {
                childrenInput.setAttribute("max", String(maxCapacity));
                if (parseInt(childrenInput.value, 10) > maxCapacity) {
                    childrenInput.value = String(maxCapacity);
                    childrenInput.dispatchEvent(new Event("change"));
                }
            }
            if (adultsInput && childrenInput && maxCapacity) {
                const adultsVal = parseInt(adultsInput.value, 10) || 0;
                const childrenVal = parseInt(childrenInput.value, 10) || 0;

                if (adultsVal + childrenVal > maxCapacity) {
                    childrenInput.value = String(Math.max(0, maxCapacity - adultsVal));
                    childrenInput.dispatchEvent(new Event("change"));
                }
            }

            const capacityLabel = selectedOpt.getAttribute("data-capacity-label");
            const adultsCapacityLabel = document.getElementById("adults-capacity-label");
            if (capacityLabel !== null && adultsCapacityLabel) {
                adultsCapacityLabel.textContent = capacityLabel;
            }

            const dogCount = selectedOpt.getAttribute("data-dog-count");
            const dogsInput = document.getElementById("dogs");
            if (dogCount !== null && dogsInput) {
                if (dogCount === "") {
                    dogsInput.removeAttribute("max");
                } else {
                    dogsInput.setAttribute("max", dogCount);
                }

                const dogsDisabled = dogCount === "0";
                dogsInput.toggleAttribute("readonly", dogsDisabled);
                dogsInput.closest(".quantitystepper")
                    ?.querySelectorAll("button")
                    .forEach((button) => {
                        button.disabled = dogsDisabled;
                    });

                if (parseInt(dogsInput.value, 10) > parseInt(dogCount, 10)) {
                    dogsInput.value = dogCount;
                    dogsInput.dispatchEvent(new Event("change"));
                }
            }

            const bookingDogs = document.getElementById("booking_dogs");
            if (bookingDogs && bookingDogs.tagName === "SELECT") {
                const maxDogs = dogCount === "" ? 5 : parseInt(dogCount || "0", 10);
                const currentVal = parseInt(bookingDogs.value, 10) || 0;

                bookingDogs.replaceChildren();

                for (let i = 0; i <= maxDogs; i++) {
                    const opt = document.createElement("option");
                    opt.value = String(i);
                    opt.textContent = global.Translator?.transChoice
                        ? global.Translator.transChoice(
                            "booking.fields.pets.values",
                            i,
                            { count: i },
                            "js"
                        )
                        : i === 0
                            ? "Keine"
                            : `${i} Haustier${i > 1 ? "e" : ""}`;
                    bookingDogs.appendChild(opt);
                }

                bookingDogs.value = String(Math.min(currentVal, maxDogs));
            }

            const dogLabel = selectedOpt.getAttribute("data-dog-label");
            const dogsCapacityLabel = document.getElementById("dogs-capacity-label");
            if (dogLabel !== null && dogsCapacityLabel) {
                dogsCapacityLabel.textContent = dogLabel;
            }

            const dateInput = document.getElementById("checkinCheckoutDates");
            if (dateInput && dateInput._flatpickr && selectedOpt.hasAttribute("data-occupied-days")) {
                const occupiedDays = JSON.parse(selectedOpt.getAttribute("data-occupied-days") || "[]");
                const checkinDays = JSON.parse(selectedOpt.getAttribute("data-checkin-days") || "[]");
                const checkoutDays = JSON.parse(selectedOpt.getAttribute("data-checkout-days") || "[]");

                dateInput._flatpickr.set("disable", [
                    function (date) {
                        const dateString = toYmd(date);
                        const isOccupied = occupiedDays.includes(dateString);
                        const isCheckin = checkinDays.includes(dateString);
                        const isCheckout = checkoutDays.includes(dateString);

                        return isOccupied && !isCheckin && !isCheckout;
                    },
                ]);
            }

            const availabilityForm = document.getElementById("availability-form");
            if (availabilityForm) {
                availabilityForm.dispatchEvent(new Event("change", { bubbles: true }));
            }

            if (dateInput && dateInput._flatpickr?.selectedDates?.length === 2) {
                checkAvailability();
            }
        });
    }

    const filterButtons = document.querySelectorAll(".js-unit-filter-btn");
    const unitCards = document.querySelectorAll(".js-rental-unit-card");

    if (filterButtons.length && unitCards.length) {
        filterButtons.forEach((btn) => {
            btn.addEventListener("click", () => {
                const targetType = btn.getAttribute("data-filter-type");

                filterButtons.forEach((b) => {
                    b.classList.remove("bg-brand-900", "text-white", "font-semibold");
                    b.classList.add("bg-gray-100", "text-brandcopy-700");
                });
                btn.classList.add("bg-brand-900", "text-white", "font-semibold");
                btn.classList.remove("bg-gray-100", "text-brandcopy-700");

                unitCards.forEach((card) => {
                    const cardType = card.getAttribute("data-unit-type");

                    card.classList.toggle(
                        "hidden",
                        !(targetType === "all" || cardType === targetType)
                    );
                });
            });
        });
    }
};

/**
 * Usability Feedback Modal after Inquiry
 */
const initFeedbackModal = () => {
    const requestsentModal = document.getElementById("requestsent-modal");
    const feedbackModal = document.getElementById("feedback-modal");

    if (!requestsentModal || !feedbackModal) {
        return;
    }

    const STORAGE_KEY = "tenant_feedback_dismissed_until";
    const SNOOZE_DAYS = 30;

    const isFeedbackSnoozed = () => {
        try {
            const dismissedUntil = localStorage.getItem(STORAGE_KEY);

            if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) {
                return true;
            }
        } catch (e) {
            // LocalStorage access issues/private mode fallback
        }

        return false;
    };

    const setFeedbackSnooze = () => {
        try {
            const expiry = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
            localStorage.setItem(STORAGE_KEY, expiry.toString());
        } catch (e) {
            // Ignore storage errors
        }
    };

    let feedbackTriggered = false;

    const showFeedbackModal = () => {
        if (feedbackTriggered || isFeedbackSnoozed()) {
            return;
        }

        feedbackTriggered = true;

        // Small delay for smooth transition after requestsent-modal closes
        setTimeout(() => {
            const instance = window.FlowbiteInstances?.getInstance("Modal", "feedback-modal");

            if (instance) {
                instance.show();
            } else {
                feedbackModal.classList.remove("hidden");
                document.body.classList.add("overflow-hidden");
            }
        }, 300);
    };

    // Close buttons on requestsent-modal ("Alles klar!" & "x")
    requestsentModal
        .querySelectorAll('[data-modal-hide="requestsent-modal"]')
        .forEach((btn) => {
            btn.addEventListener("click", () => {
                showFeedbackModal();
            });
        });

    // Buttons inside feedback modal: record choice to snooze for 30 days
    feedbackModal
        .querySelectorAll("[data-feedback-action]")
        .forEach((btn) => {
            btn.addEventListener("click", () => {
                setFeedbackSnooze();
            });
        });
};
